import { DesktopAppDownload } from '../components/DesktopAppDownload.js';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bot,
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  FileText,
  Gauge,
  Images,
  Mail,
  Pencil,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  capLimits,
  LANDING_SECTION_KINDS,
  landingCopy,
  round2,
  type LandingCopy,
  type LandingPage as LandingPageData,
  type LandingSection,
  type LandingSectionKind,
  type PlanDefinition,
} from '@worki/shared';
import { api, RequestError } from '../lib/api.js';
import { apiUrl } from '../lib/config.js';
import { currentLocale } from '../i18n.js';
import { money } from '../lib/format.js';
import { useSession } from '../lib/session.js';
import {
  Button,
  ErrorNotice,
  Field,
  Input,
  LoadingScreen,
  Select,
  Textarea,
} from '../components/ui.js';
import { ConfirmDialog, Modal } from '../components/Modal.js';
import { GoogleButton } from '../components/GoogleButton.js';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import { WorkiLogo } from '../components/WorkiLogo.js';
import { useToast } from '../components/Toast.js';
import { cn } from '../lib/cn.js';
import { landingVisualFor, type LandingVisualKey } from './landingVisuals.js';
import '../styles/landing.css';

/**
 * MKT-001 — the marketing page, and the screen that edits it.
 *
 * One component for both because they are the same page: a visitor sees the
 * sections, and whoever sells the product sees the same sections with a toolbar
 * on each. Editing somewhere else would mean writing the copy against a preview
 * of the page rather than against the page.
 *
 * The words come from the locale files through `landingCopy`, and an edit made
 * here is stored as an override of them for the language it was typed in — so
 * the page stays translated, and "restore the original" is a delete rather than
 * a re-typing. Prices are not stored at all: the cards below are rendered from
 * the live price list, so a change in the back office is on the page at once.
 */

async function publicRequest<T>(path: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(apiUrl(`/public${path}`), { headers: { 'Content-Type': 'application/json' } });
  } catch {
    throw new RequestError(0, 'errors.network');
  }

  const payload: unknown = response.headers.get('content-type')?.includes('application/json')
    ? await response.json()
    : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string } } | null)?.error;
    throw new RequestError(response.status, error?.code ?? 'errors.unknown');
  }
  return payload as T;
}

/** What a section carries besides its words — the parts an editor also sets. */
interface SectionDraft {
  kind: LandingSectionKind;
  ctaHref: string;
  copy: LandingCopy;
}

export function LandingPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const { session } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();

  /*
   * `/platform/me` answers every signed-in user, but it answers 401 for a
   * visitor — and a 401 here would drop a public page into the signed-out
   * handling. So it is only asked once there is a session to ask about.
   */
  const staffQuery = useQuery({
    queryKey: ['platform-me'],
    queryFn: () => api.get<{ isPlatformAdmin: boolean }>('/platform/me'),
    enabled: Boolean(session),
    staleTime: 5 * 60_000,
  });
  const canEdit = Boolean(session) && staffQuery.data?.isPlatformAdmin === true;

  const pageQuery = useQuery({
    queryKey: ['landing', canEdit],
    queryFn: () =>
      canEdit
        ? api.get<LandingPageData>('/platform/landing')
        : publicRequest<LandingPageData>('/landing'),
  });

  const [editing, setEditing] = useState<LandingSection | 'new' | null>(null);
  const [confirming, setConfirming] = useState<{
    action: 'remove' | 'restore';
    section: LandingSection;
  } | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['landing'] });
  const failed = () => toast.error(t('common.error'));

  const save = useMutation({
    mutationFn: ({ section, draft }: { section: LandingSection | null; draft: SectionDraft }) => {
      const body = {
        kind: draft.kind,
        contentKey: section?.contentKey ?? null,
        ctaHref: draft.ctaHref.trim() === '' ? null : draft.ctaHref.trim(),
        hidden: section?.hidden ?? false,
        copy: { ...(section?.overrides ?? {}), [locale]: draft.copy },
      };
      return section
        ? api.patch<LandingSection>(`/platform/landing/${section.id}`, body)
        : api.post<LandingSection>('/platform/landing', body);
    },
    onSuccess: () => {
      setEditing(null);
      toast.success(t('common.saved'));
      void refresh();
    },
    onError: failed,
  });

  /** Restoring the original text is deleting the edit, not typing it back in. */
  const restore = useMutation({
    mutationFn: (section: LandingSection) => {
      const copy = { ...section.overrides };
      delete copy[locale];
      return api.patch<LandingSection>(`/platform/landing/${section.id}`, {
        kind: section.kind,
        contentKey: section.contentKey,
        ctaHref: section.ctaHref,
        hidden: section.hidden,
        copy,
      });
    },
    onSuccess: () => {
      setEditing(null);
      void refresh();
    },
    onError: failed,
  });

  const setHidden = useMutation({
    mutationFn: (section: LandingSection) =>
      api.patch<LandingSection>(`/platform/landing/${section.id}`, {
        kind: section.kind,
        contentKey: section.contentKey,
        ctaHref: section.ctaHref,
        hidden: !section.hidden,
        copy: section.overrides,
      }),
    onSuccess: refresh,
    onError: failed,
  });

  const move = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: 'up' | 'down' }) =>
      api.post<LandingSection[]>(`/platform/landing/${id}/move`, { direction }),
    onSuccess: refresh,
    onError: failed,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del<void>(`/platform/landing/${id}`),
    onSuccess: () => {
      toast.success(t('common.deleted'));
      void refresh();
    },
    onError: failed,
  });

  if (pageQuery.isLoading) return <LoadingScreen />;
  if (pageQuery.isError || !pageQuery.data) {
    return (
      <div className="public-page">
        <div className="mx-auto max-w-3xl">
          <ErrorNotice code={(pageQuery.error as RequestError | null)?.code ?? 'errors.unknown'} />
        </div>
      </div>
    );
  }

  const page = pageQuery.data;

  return (
    <div className="public-page landing-page">
      <div className="landing-ambient" aria-hidden="true" />
      <header className="landing-nav">
        <div className="landing-nav__inner">
          <WorkiLogo className="landing-wordmark" title={t('common.appName')} />
          {canEdit && (
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary">
              {t('landing.editor.badge')}
            </span>
          )}
          <div className="landing-nav__actions">
            <LanguageSwitcher />
            {!session && (
              <Link to="/login" className="landing-sign-in">
                {t('landing.signIn')}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="landing-main">

        {canEdit && <p className="landing-editor-hint">{t('landing.editor.hint')}</p>}

        {page.sections.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('landing.editor.empty')}</p>
        )}

        {page.sections.map((section, index) => (
          <section
            key={section.id}
            className={cn(
              'landing-section relative',
              `landing-section--${section.kind}`,
              section.hidden && 'opacity-50',
            )}
          >
            {canEdit && (
              <div className="landing-editor-toolbar">
                <Button
                  size="sm"
                  icon={<Pencil className="h-3.5 w-3.5" />}
                  onClick={() => setEditing(section)}
                >
                  {t('landing.editor.edit')}
                </Button>
                <Button
                  size="sm"
                  icon={section.hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  onClick={() => setHidden.mutate(section)}
                >
                  {section.hidden ? t('landing.editor.show') : t('landing.editor.hide')}
                </Button>
                {/* Buttons, not dragging — the product has no drag-and-drop, and
                    a page reordered on a phone would be the one place it did. */}
                <Button
                  size="sm"
                  aria-label={t('common.moveUp')}
                  disabled={index === 0}
                  icon={<ChevronUp className="h-3.5 w-3.5" />}
                  onClick={() => move.mutate({ id: section.id, direction: 'up' })}
                />
                <Button
                  size="sm"
                  aria-label={t('common.moveDown')}
                  disabled={index === page.sections.length - 1}
                  icon={<ChevronDown className="h-3.5 w-3.5" />}
                  onClick={() => move.mutate({ id: section.id, direction: 'down' })}
                />
                <Button
                  size="sm"
                  variant="danger"
                  icon={<Trash2 className="h-3.5 w-3.5" />}
                  onClick={() => setConfirming({ action: 'remove', section })}
                >
                  {t('landing.editor.remove')}
                </Button>
                {section.hidden && (
                  <span className="text-[11px] text-muted-foreground">{t('landing.editor.hidden')}</span>
                )}
              </div>
            )}

            <SectionBody section={section} page={page} index={index} />
            {section.kind === 'hero' && <DesktopAppDownload accountKey={session ? `${session.business.id}:${session.user.id}` : 'public'} />}
          </section>
        ))}

        {canEdit && (
          <div>
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
              {t('landing.editor.add')}
            </Button>
          </div>
        )}
      </main>

      {/* LEGAL-001 — the two documents Play and the OAuth consent screen link
          to. Not editable through the section editor: a marketing page whose
          privacy link can be renamed or removed by whoever is editing copy is
          a compliance problem waiting to happen. */}
      <footer className="landing-footer">
        <Link to="/privacy">{t('legal.privacy.title')}</Link>
        <Link to="/terms">{t('legal.terms.title')}</Link>
        <Link to="/accessibility">{t('legal.accessibility.title')}</Link>
        <Link to="/delete-account">{t('legal.deleteAccount.title')}</Link>
      </footer>

      {editing && (
        <SectionEditor
          section={editing === 'new' ? null : editing}
          saving={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(draft) => save.mutate({ section: editing === 'new' ? null : editing, draft })}
          onRestore={
            editing !== 'new' && editing.contentKey && editing.overrides[locale]
              ? () => setConfirming({ action: 'restore', section: editing })
              : undefined
          }
        />
      )}

      {confirming && (
        <ConfirmDialog
          open
          title={
            confirming.action === 'remove'
              ? t('landing.editor.remove')
              : t('landing.editor.reset')
          }
          message={
            confirming.action === 'remove'
              ? t('landing.editor.removeConfirm')
              : t('landing.editor.resetConfirm')
          }
          confirmLabel={
            confirming.action === 'remove' ? t('common.delete') : t('landing.editor.reset')
          }
          loading={remove.isPending || restore.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            if (confirming.action === 'remove') remove.mutate(confirming.section.id);
            else restore.mutate(confirming.section);
            setConfirming(null);
          }}
        />
      )}
    </div>
  );
}

/** Where the hero's sign-in card sits, so a CTA lower down can reach it. */
export const SIGN_IN_ANCHOR = 'landing-signin';

/**
 * MKT-002 — the way in, on the page itself.
 *
 * Small on purpose: it is a door beside the argument, not the argument. The
 * page used to send every visitor to a registration form before it had made its
 * case, which asks for a company number from somebody who has not decided
 * anything yet. So the card only ever asks to be let in — by Google, or by a
 * code to an address — and opening an account is what happens *after* one of
 * those, with the address already proven.
 *
 * Nothing here says whether an address is known to Worki. `/auth/request-login-code`
 * answers the same way either way, deliberately, so the offer to open an account
 * is made to everyone who asks for a code rather than only to strangers.
 */
function SignInCard() {
  const { t } = useTranslation();
  const { session, login } = useSession();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (session) {
    return (
      <aside className="landing-signin" id={SIGN_IN_ANCHOR}>
        <p className="landing-signin__title">{t('landing.signedInTitle', { name: session.user.name })}</p>
        <Link to="/" className="landing-primary-cta landing-signin__go">
          {t('landing.signedInCta')}
        </Link>
      </aside>
    );
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (step === 'email') {
        await api.post('/auth/request-login-code', { email });
        setStep('code');
      } else {
        await login({ email, code });
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err instanceof RequestError ? err.code : 'errors.unknown');
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="landing-signin" id={SIGN_IN_ANCHOR}>
      <p className="landing-signin__title">{t('landing.signInTitle')}</p>
      <p className="landing-signin__hint">
        {step === 'email' ? t('landing.signInHint') : t('auth.codeSent', { email })}
      </p>

      <form className="landing-signin__form" onSubmit={submit}>
        {error && <ErrorNotice code={error} />}

        {/* Only before a code is on its way: a second way in, offered beside a
            code that has already been sent, is a way to lose the code. */}
        {step === 'email' && <GoogleButton />}

        <Field label={t('auth.email')} htmlFor="landing-email">
          <Input
            id="landing-email"
            type="email"
            autoComplete="email"
            dir="ltr"
            value={email}
            disabled={step === 'code'}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        {step === 'code' && (
          <Field label={t('auth.otp')} htmlFor="landing-code" hint={t('auth.codeExpiry')}>
            <Input
              id="landing-code"
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </Field>
        )}

        <Button
          type="submit"
          variant="primary"
          block
          loading={busy}
          disabled={step === 'email' ? !email : code.length !== 6}
        >
          {step === 'email' ? t('auth.sendCode') : t('auth.login')}
        </Button>

        {/* The registration form, and the first point at which it is offered:
            the address is typed, the code is sent, and somebody who turns out
            not to have an account can carry that address straight into it. */}
        {step === 'code' && (
          <p className="landing-signin__foot">
            {t('landing.noAccount')}{' '}
            <Link to={`/signup?email=${encodeURIComponent(email)}`}>{t('auth.signup')}</Link>
          </p>
        )}
      </form>
    </aside>
  );
}

/**
 * A line of copy, and the badge it ends in.
 *
 * A short trailing parenthetical — "(בקרוב)" — is drawn beside the line rather
 * than as more of the sentence. It lives inside the copy so a promise can be
 * marked, or unmarked once it ships, from the section editor: no deploy, and no
 * list in this file of which features are finished. Anything longer than a
 * label is left alone, because then it is a real aside and not a badge.
 */
function withNote(line: string): { text: string; note: string | null } {
  const marked = /^(.*?)\s*\(([^()]{1,20})\)$/.exec(line.trim());
  return { text: marked?.[1] ?? line, note: marked?.[2] ?? null };
}

/** One section as a visitor sees it, whoever is looking. */
function SectionBody({
  section,
  page,
  index,
}: {
  section: LandingSection;
  page: LandingPageData;
  index: number;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const copy = landingCopy(section, locale);
  const visual = landingVisualFor(section.contentKey);

  const label = withNote(copy.eyebrow ?? '');
  const eyebrow = copy.eyebrow && (
    <p className="landing-eyebrow">
      {label.text}
      {label.note && <em className="landing-pill__note">{label.note}</em>}
    </p>
  );
  /*
   * A call to action that used to open the registration form now moves the
   * page to the card in the hero. Nobody is asked for a company number before
   * they have been let in, and the button still does what its label promises —
   * it is the same first step, one screen earlier.
   */
  const cta = copy.ctaLabel && section.ctaHref && (
    section.ctaHref === '/signup' ? (
      <a href={`#${SIGN_IN_ANCHOR}`} className="landing-primary-cta">
        {copy.ctaLabel}
      </a>
    ) : (
      <Link to={section.ctaHref} className="landing-primary-cta">
        {copy.ctaLabel}
      </Link>
    )
  );

  if (section.kind === 'hero') {
    return (
      <div className="landing-hero">
        <div className="landing-hero__copy">
          {eyebrow}
          {copy.heading && <h1>{copy.heading}</h1>}
          {copy.body && <p className="landing-lede">{copy.body}</p>}
          {copy.bullets.length > 0 && (
            <ul className="landing-benefit-pills">
              {copy.bullets.map((line) => {
                const pill = withNote(line);
                return (
                  <li key={line}>
                    <Check aria-hidden="true" />
                    <span>{pill.text}</span>
                    {pill.note && <em className="landing-pill__note">{pill.note}</em>}
                  </li>
                );
              })}
            </ul>
          )}
          {cta}
        </div>
        {/* Where the screenshot was. A page with no pictures on it still needs
            somewhere for the eye to land, and the thing worth landing on is the
            way in. */}
        <div className="landing-hero__visual">
          <span className="landing-ai-orbit" aria-hidden="true">
            <Sparkles />
          </span>
          <SignInCard />
        </div>
      </div>
    );
  }

  if (section.kind === 'plans') {
    return (
      <div className="landing-plans">
        <div className="landing-section-heading">
          {eyebrow}
          {copy.heading && <h2>{copy.heading}</h2>}
          {copy.body && <p>{copy.body}</p>}
        </div>
        <div className="landing-plan-grid">
          {page.plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
        <p className="landing-trial-note">
          {t('landing.plansTrial', { days: page.trialDays })}
        </p>
      </div>
    );
  }

  if (section.kind === 'cta') {
    return (
      <div className="landing-closing">
        <div className="landing-closing__glow" aria-hidden="true" />
        <div className="landing-closing__content">
          {eyebrow}
          {copy.heading && <h2>{copy.heading}</h2>}
          {copy.body && <p>{copy.body}</p>}
          {cta}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'landing-feature',
        index % 2 === 0 && 'landing-feature--reversed',
        visual?.key === 'ai-agents' && 'landing-feature--ai',
        // Every feature is text-only now: the column that held a screenshot of
        // the screen being described is gone, and a two-column grid with one
        // column left in it is half a page of white space.
        'landing-feature--text-only',
      )}
    >
      <div className="landing-feature__copy">
        {visual && (
          <span className="landing-feature__icon" aria-hidden="true">
            <FeatureIcon kind={visual.key} />
          </span>
        )}
        {eyebrow}
        {copy.heading && <h2>{copy.heading}</h2>}
        {copy.body && <p>{copy.body}</p>}
        {copy.bullets.length > 0 && (
          <ul className="landing-feature-list">
            {copy.bullets.map((line) => (
              <li key={line}>
                <span className="landing-check" aria-hidden="true">
                  <Check />
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        )}
        {cta}
      </div>
    </div>
  );
}

function FeatureIcon({ kind }: { kind: LandingVisualKey }) {
  if (kind === 'dashboard') return <Gauge />;
  if (kind === 'quotes') return <FileText />;
  if (kind === 'gallery') return <Images />;
  if (kind === 'invoices') return <Mail />;
  return <Bot />;
}

/**
 * A plan, priced from the live list.
 *
 * The names, the caption and the caps are the billing screen's own words:
 * a second set of plan labels written for the marketing page is a second set to
 * keep in step with the first.
 */
function PlanCard({ plan }: { plan: PlanDefinition }) {
  const { t } = useTranslation();
  const locale = currentLocale();

  // The plan picker's cap labels ("a month", not "this month"): a price list
  // states what a plan allows, it does not count what was used. Capped like the
  // server caps them, or pro reads "unlimited galleries" and stops at twenty.
  const limits = capLimits(plan.limits);
  const caps: Array<[string, number | null]> = [
    ['billing.limitLabel.quotesPerMonth', limits.quotesPerMonth],
    ['billing.limitLabel.voiceActionsPerMonth', limits.voiceActionsPerMonth],
    ['billing.limitLabel.galleries', limits.galleries],
    ['billing.limitLabel.connectedMailAccounts', limits.connectedMailAccounts],
  ];

  return (
    <section className={cn('landing-plan-card', plan.id === 'business' && 'landing-plan-card--featured')}>
      <h3>{t(`billing.plans.${plan.id}`)}</h3>
      <p className="landing-plan-price">
        {/* Stored in agorot; the shared formatter owns the symbol and its side. */}
        <span className="ltr-nums">
          {money(round2(plan.monthlyPriceAgorot / 100), 'ILS', locale)}
        </span>
        <span>{t('billing.perMonth')}</span>
      </p>
      <p className="landing-plan-caption">{t(`billing.planTagline.${plan.id}`)}</p>
      <ul className="landing-plan-caps">
        {caps.map(([key, value]) => (
          <li key={key}>
            <Check aria-hidden="true" />
            <span>
              {t(key)}: {value === null ? t('billing.unlimited') : value === 0 ? t('billing.notIncluded') : value}
            </span>
          </li>
        ))}
      </ul>
      <a href={`#${SIGN_IN_ANCHOR}`} className="landing-plan-cta">
        {t('billing.choose')}
      </a>
    </section>
  );
}

/**
 * The editor for one section.
 *
 * It opens on what the page is showing right now — the shipped text when there
 * is no edit yet — so rewriting a sentence starts from the sentence rather than
 * from an empty box.
 */
function SectionEditor({
  section,
  saving,
  onClose,
  onSave,
  onRestore,
}: {
  section: LandingSection | null;
  saving: boolean;
  onClose: () => void;
  onSave: (draft: SectionDraft) => void;
  /** Absent for a section that has nothing shipped to go back to. */
  onRestore?: () => void;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const current = section
    ? landingCopy(section, locale)
    : { eyebrow: null, heading: null, body: null, bullets: [], ctaLabel: null };

  const [kind, setKind] = useState<LandingSectionKind>(section?.kind ?? 'feature');
  const [eyebrow, setEyebrow] = useState(current.eyebrow ?? '');
  const [heading, setHeading] = useState(current.heading ?? '');
  const [body, setBody] = useState(current.body ?? '');
  const [bullets, setBullets] = useState(current.bullets.join('\n'));
  const [ctaLabel, setCtaLabel] = useState(current.ctaLabel ?? '');
  const [ctaHref, setCtaHref] = useState(section?.ctaHref ?? '');

  const submit = () =>
    onSave({
      kind,
      ctaHref,
      copy: {
        eyebrow: eyebrow.trim() || null,
        heading: heading.trim() || null,
        body: body.trim() || null,
        bullets: bullets
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0),
        ctaLabel: ctaLabel.trim() || null,
      },
    });

  return (
    <Modal
      open
      title={section ? t('landing.editor.edit') : t('landing.editor.newSection')}
      onClose={onClose}
      footer={
        <div className="flex w-full items-center gap-2">
          {onRestore && (
            <Button icon={<RotateCcw className="h-4 w-4" />} onClick={onRestore}>
              {t('landing.editor.reset')}
            </Button>
          )}
          <div className="ms-auto flex gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="primary" loading={saving} onClick={submit}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label={t('landing.editor.kind')}>
          <Select
            value={kind}
            onChange={(value) => setKind(value as LandingSectionKind)}
            options={LANDING_SECTION_KINDS.map((value) => ({
              value,
              label: t(`landing.kind.${value}`),
            }))}
          />
        </Field>
        <Field label={t('landing.editor.eyebrow')}>
          <Input value={eyebrow} onChange={(event) => setEyebrow(event.target.value)} />
        </Field>
        <Field label={t('landing.editor.heading')}>
          <Input value={heading} onChange={(event) => setHeading(event.target.value)} />
        </Field>
        <Field label={t('landing.editor.body')}>
          <Textarea value={body} onChange={(event) => setBody(event.target.value)} rows={4} />
        </Field>
        <Field label={t('landing.editor.bullets')} hint={t('landing.editor.bulletsHint')}>
          <Textarea value={bullets} onChange={(event) => setBullets(event.target.value)} rows={4} />
        </Field>
        <Field label={t('landing.editor.ctaLabel')}>
          <Input value={ctaLabel} onChange={(event) => setCtaLabel(event.target.value)} />
        </Field>
        <Field label={t('landing.editor.ctaHref')} hint={t('landing.editor.ctaHrefHint')}>
          <Input value={ctaHref} onChange={(event) => setCtaHref(event.target.value)} dir="ltr" />
        </Field>
      </div>
    </Modal>
  );
}
